/**
 * EventTreeNav — the Event Detail page's navigator: the event itself ("Overall"), then every event
 * beneath it at any depth, as a tree. Selecting a row opens that event's details beside it.
 *
 * Replaces the page's tab bar, which could only hold one level (PM request: show every nested child
 * event, and show it as the tree it is). The rows come from utils/eventTree `visibleEventTree`, so
 * what is listed — and what is not — is decided there, not here.
 *
 * Looks like the Event Hierarchy field (CampaignHierarchyView): the same nested <ul>/<li> and
 * connector lines, so the two read as one structure. An event row is a button carrying its name,
 * status and dates; an external campaign is a grey label that groups the events under it but opens
 * nothing (it has no detail of its own here). Expanded by default — real trees are a handful of
 * events, and the point is to see them — with a toggle on every branch for the ones that are not.
 *
 * A name filter appears past SEARCH_THRESHOLD events, where scanning stops being quicker than typing.
 * While it is in use every branch shows, collapsed or not, so no match is hidden by an earlier toggle.
 *
 * Hand-rendered rather than Cloudscape's TreeView, which is not exported at this version (the same
 * reason as CampaignHierarchy).
 */

import { useMemo, useState } from 'react';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import TextFilter from '@amzn/awsui-components-react/polaris/text-filter';
import { formatRange } from '../../utils/eventDates';
import { eventRows, filterEventTree, type EventTreeRow } from '../../utils/eventTree';
import '../CampaignHierarchy/CampaignHierarchy.css';
import './EventTreeNav.css';

/** More events than this, and the navigator offers a name filter. */
export const SEARCH_THRESHOLD = 15;

export interface EventTreeNavProps {
  /** The page's own event, the "Overall" row. */
  rootName?: string;
  tree: readonly EventTreeRow[];
  /** The open event's id; null for Overall. */
  selectedEventId: string | null;
  /** Opens an event (its id) or Overall (null). */
  onSelect: (eventId: string | null) => void;
}

interface RowContext {
  selectedEventId: string | null;
  onSelect: (eventId: string | null) => void;
  collapsed: ReadonlySet<string>;
  toggle: (key: string) => void;
  /** While filtering, every branch is open. */
  ignoreCollapse: boolean;
}

function Toggle({ row, label, ctx }: { row: EventTreeRow; label: string; ctx: RowContext }) {
  if (!row.children.length || ctx.ignoreCollapse) return <span className="eh-hierarchy__toggle" />;
  const open = !ctx.collapsed.has(row.key);
  return (
    <span className="eh-hierarchy__toggle">
      <Button
        variant="inline-icon"
        iconName={open ? 'caret-down-filled' : 'caret-right-filled'}
        ariaLabel={`${open ? 'Collapse' : 'Expand'} ${label}`}
        ariaExpanded={open}
        onClick={() => ctx.toggle(row.key)}
      />
    </span>
  );
}

function Row({ row, ctx }: { row: EventTreeRow; ctx: RowContext }) {
  const open = ctx.ignoreCollapse || !ctx.collapsed.has(row.key);
  const label =
    row.kind === 'event' ? row.event.eventName || row.event.eventId : row.sfdcCampaignId || '—';
  return (
    <li className="eh-hierarchy__item">
      <div className="eh-hierarchy__row">
        <Toggle row={row} label={label} ctx={ctx} />
        {row.kind === 'event' ? (
          <button
            type="button"
            className="eh-tree-nav__select"
            aria-current={ctx.selectedEventId === row.event.eventId ? 'true' : undefined}
            onClick={() => ctx.onSelect(row.event.eventId)}
          >
            <span className="eh-tree-nav__line">
              <span className="eh-tree-nav__name">{label}</span>
              {row.event.contentStatus === 'DRAFT' ? <Badge color="blue">Draft</Badge> : null}
            </span>
            <Box color="text-body-secondary" fontSize="body-s">
              {formatRange(row.event.startDate, row.event.endDate)}
            </Box>
          </button>
        ) : (
          <span className="eh-tree-nav__campaign eh-tree-nav__line" title="External SFDC campaign">
            <Icon name="folder" variant="subtle" />
            <Box variant="code" color="text-body-secondary">
              {label}
            </Box>
          </span>
        )}
      </div>
      {row.children.length && open ? (
        <ul className="eh-hierarchy__children">
          {row.children.map((c) => (
            <Row key={c.key} row={c} ctx={ctx} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function EventTreeNav({
  rootName,
  tree,
  selectedEventId,
  onSelect,
}: EventTreeNavProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState('');
  const total = useMemo(() => eventRows(tree).length, [tree]);
  const searchable = total > SEARCH_THRESHOLD;
  const filtering = searchable && !!query.trim();
  const shown = useMemo(
    () => (filtering ? filterEventTree(tree, query) : tree),
    [filtering, tree, query],
  );

  const ctx: RowContext = {
    selectedEventId,
    onSelect,
    collapsed,
    ignoreCollapse: filtering,
    toggle: (key) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
  };

  return (
    <nav aria-label="Events in this hierarchy">
      {searchable ? (
        <Box padding={{ bottom: 's' }}>
          <TextFilter
            filteringText={query}
            filteringPlaceholder="Find an event"
            filteringAriaLabel="Find an event by name"
            onChange={({ detail }) => setQuery(detail.filteringText)}
          />
        </Box>
      ) : null}
      <ul className="eh-hierarchy">
        <li className="eh-hierarchy__item">
          <div className="eh-hierarchy__row eh-hierarchy__row--root">
            <button
              type="button"
              className="eh-tree-nav__select"
              aria-current={selectedEventId === null ? 'true' : undefined}
              onClick={() => onSelect(null)}
            >
              <span className="eh-tree-nav__line">
                <Icon name="calendar" />
                <Box fontWeight="bold">Overall</Box>
              </span>
              {rootName ? (
                <Box color="text-body-secondary" fontSize="body-s">
                  {rootName}
                </Box>
              ) : null}
            </button>
          </div>
          {shown.length ? (
            <ul className="eh-hierarchy__children">
              {shown.map((r) => (
                <Row key={r.key} row={r} ctx={ctx} />
              ))}
            </ul>
          ) : (
            <Box color="text-body-secondary" padding={{ left: 's' }}>
              No event matches.
            </Box>
          )}
        </li>
      </ul>
    </nav>
  );
}
